import * as React from 'react'
import { Outlet, useOutletContext } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'
import { AvailableModules, Features } from 'generated/featureDefinitions'

import Upgrade from 'modules/core/components/Upgrade'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { DefaultNotification } from 'modules/expenses/components/_Notification'
import CardsBanner from 'modules/expenses/components/common/CardsBanner'
import ExpensesContent from 'modules/expenses/components/expenses/PageContents/ExpensesContent'
import { useManagedExpensesLimits } from 'modules/expenses/hooks/useManagedExpensesLimits'
import type {
  AddUsersDisabledReason,
  RouteContextParams,
} from 'modules/expenses/lib/RouteContextParams'
import { useExpenses } from 'modules/expenses/pages/ExpensesContext'
import useShowCardsBanner from 'modules/expenses/pages/ExpensesListPage/hooks/useShowCardsBanner'
import AddUsersModal from 'modules/expenses/pages/subscription/AddUsersModal'
import { missingFeatureRequestReason } from 'modules/finance/lib/missingFeatureRequestReason'
import { UpsellingKit } from 'modules/upselling_kit/components'

import { ExpensesListPageLayout } from './Layout'

const upgradeMessage = (
  limitReached: boolean,
  limitNearlyReached: boolean,
  usageLimit?: number | null | undefined
) => {
  if (limitReached) {
    return i18n.t('expenses_v2.alerts.managed_expenses_limit_reached', {
      limit: usageLimit,
    })
  }
  if (limitNearlyReached) {
    return i18n.t('expenses_v2.alerts.managed_expenses_limit_nearly_reached', {
      limit: usageLimit,
    })
  }
}

const ExpensesListPage = () => {
  const { expensableGroupsEnabled, bankingAccount, onboarding, inExpiredFreeTrial, ...context } =
    useOutletContext<RouteContextParams>()
  const [upgradeSubscription, setUpgradeSubscription] = React.useState(false)
  const [addUsersModalOpen, setAddUsersModalOpen] = React.useState(false)
  const bankAccount = bankingAccount?.bankAccountsConnection.nodes[0]

  const { data, featureAvailabilities } = useExpenses()

  const {
    loading: loadingAvailabilities,
    limitNearlyReached,
    limitReached,
    usageLimit,
  } = useManagedExpensesLimits()

  const {
    company,
    access: { locale },
  } = useCurrentSession()

  const { showCardsBanner, onDismiss: onDismissCardsBanner } = useShowCardsBanner({
    canManageCards: data.expenses._policies.expenseSetToPaid.can,
  })

  const managedByPartner = data.features.managedByPartner.nodes.some(
    (el) => el.name === 'managed_by_partner' && el.enabled
  )
  const expensesUserCount = data.features.featureMembership?.membersConnection.totalCount ?? 0
  const expensesUserLimit =
    data?.apiCore?.currentsConnection?.nodes[0]?.company?.subscription?.expensesQuantityCeiling ?? 0

  const addUsersDisabledReason: AddUsersDisabledReason | undefined = (() => {
    if (!managedByPartner) return undefined
    if (expensesUserLimit <= 0) return 'no_ceiling'
    if (expensesUserCount >= expensesUserLimit) return 'limit_reached'
    return undefined
  })()

  useEffect(() => {
    track(trackingEvents.expenses.goToExpenses, { context: 'company' })
  }, [])

  const message = upgradeMessage(limitReached, limitNearlyReached, usageLimit)

  return (
    <ExpensesListPageLayout useDataCollectionLayout={featureAvailabilities.dataCollectionEnabled}>
      {showCardsBanner && <CardsBanner onDismiss={onDismissCardsBanner} />}
      {!loadingAvailabilities && !!message && (
        <Box paddingTop="s32">
          <Upgrade.Banner
            message={message}
            button={
              <Button type="upgrade" onClick={() => setUpgradeSubscription(true)}>
                {i18n.t('packaging.upgrade_plan.buttons.know_more')}
              </Button>
            }
          />
        </Box>
      )}
      {onboarding?.status && (
        <DefaultNotification
          bankAccount={bankAccount}
          bankAddress={bankingAccount?.bankAddress}
          onboardingStatus={onboarding?.status}
          onboardingDeadline={onboarding?.deadline}
          locale={locale}
        />
      )}
      <Box flexDirection="column" flexGrow gapDivider={showCardsBanner} style={{ minHeight: '0' }}>
        <ExpensesContent expensableGroupsEnabled={expensableGroupsEnabled}>
          <Outlet
            context={{
              ...context,
              inExpiredFreeTrial,
              ...(featureAvailabilities.expensesPerUserPricing
                ? {
                    onAddUsers: () => setAddUsersModalOpen(true),
                    addUsersDisabledReason,
                  }
                : {}),
            }}
          />
        </ExpensesContent>
      </Box>

      {upgradeSubscription && (
        <UpsellingKit.ProductModal
          isOpen={upgradeSubscription}
          onClose={() => setUpgradeSubscription(false)}
          requestSource="Expenses list page"
          featureRequested={Features.expenses_monthly_managed_expenses}
          moduleRequested={AvailableModules.expenses}
          requestReason={missingFeatureRequestReason(company)}
          pageSource="expenses_list_page"
        />
      )}
      {addUsersModalOpen && (
        <AddUsersModal disabledReason={addUsersDisabledReason} onClose={() => setAddUsersModalOpen(false)} />
      )}
    </ExpensesListPageLayout>
  )
}

export default ExpensesListPage
